import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import { leadAvatarClass, initials } from '../lib/leadAvatar';

/**
 * GradientAvatar — single source of truth for the deterministic gradient
 * avatar used across Header, Lead Nurture, Meetings, Leaderboards, etc.
 *
 * Always wraps the avatar in a hover Tooltip with the person's full name +
 * role, so the bare initials never feel anonymous.
 *
 * Props
 * ─────
 *   name      string — full display name (used for initials + tooltip)
 *   role      string — e.g. "Broker", "Admin", "Client" (subtitle in tooltip)
 *   keyId     string — stable id used to pick the gradient slot (so the same
 *                      person always renders the same colour). Falls back to
 *                      `name` when omitted.
 *   size      'xs' | 'sm' | 'md' | 'lg' — preset sizes (default 'sm')
 *   className extra Tailwind classes appended to the avatar div
 *   testId    optional data-testid for the avatar element
 *   subtitle  optional extra line (e.g. lead status) shown below the role
 *   side      tooltip preferred side, default 'bottom'
 */
const SIZE_MAP = {
  xs: 'w-6 h-6 text-[9px]',
  sm: 'w-8 h-8 text-[10px]',
  md: 'w-10 h-10 text-xs',
  lg: 'w-12 h-12 text-sm',
};

const GradientAvatar = ({
  name,
  role,
  keyId,
  size = 'sm',
  className = '',
  testId,
  subtitle,
  side = 'bottom',
  imageUrl,
}) => {
  const sizeClass = SIZE_MAP[size] || SIZE_MAP.sm;
  const gradient = leadAvatarClass(keyId || name);
  const initialsText = initials(name);

  const trigger = imageUrl ? (
    <img
      src={imageUrl}
      alt={name ? `${name}${role ? `, ${role}` : ''}` : 'User avatar'}
      className={`rounded-full object-cover shadow-sm select-none ${sizeClass} ${className}`}
      data-testid={testId}
    />
  ) : (
    <div
      className={`rounded-full flex items-center justify-center text-white font-bold shadow-sm select-none cursor-default ${sizeClass} ${gradient} ${className}`}
      data-testid={testId}
      aria-label={name ? `${name}${role ? `, ${role}` : ''}` : 'User avatar'}
    >
      {initialsText}
    </div>
  );

  // If we have nothing to show in the tooltip (no name AND no role), render
  // the avatar bare — the tooltip wrapper would just be visual noise.
  if (!name && !role && !subtitle) return trigger;

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>{trigger}</TooltipTrigger>
        <TooltipContent
          side={side}
          className="bg-navy-surface text-white border border-white/10 px-3 py-2 max-w-xs"
        >
          <div className="flex flex-col gap-0.5 text-left">
            <span className="text-sm font-bold leading-tight">{name || 'Unknown'}</span>
            {role && (
              <span className="text-[10px] uppercase tracking-widest text-crm-blue-light font-semibold">
                {role}
              </span>
            )}
            {subtitle && (
              <span className="text-[11px] text-slate-300 font-medium">{subtitle}</span>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default GradientAvatar;
